import { AfterViewChecked, AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { StateChange } from 'ng-lazyload-image';
import { Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { Category } from 'src/@omnial/_models/catalog/category.model';
import { Product, ProductDetail } from 'src/@omnial/_models/catalog/product.model';
import { CatalogService } from 'src/@omnial/_services/catalog/catalog.service';
import { TrackingService } from 'src/@omnial/_services/external/tracking.service';
import { CanonicalService } from 'src/@omnial/_services/navigation/canonical.service';
import { environment } from 'src/environments/environment';
import { ProductDialogComponent } from 'src/@omnial/shared/product-dialog/product-dialog.component';
import { AppSettings } from 'src/app/app.settings';
import { SwiperOptions } from 'swiper/types';

@Component({
    selector: 'app-products-carousel',
    templateUrl: './../../../app/shared/products-carousel/products-carousel.component.html',
    styleUrls: ['./../../../app/shared/products-carousel/products-carousel.component.scss'],
    standalone: false
})
export class ProductsCarouselComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() products: Product[];
  @Input() carouselName: string;
  @Input() listId: string;
  @Input() count1700 = 4;
  @Input() count1280 = 4;
  @Input() count960 = 4;
  @Input() count740 = 3;
  @Input() count480 = 2;
  @ViewChildren('swiper') swiper: QueryList<ElementRef>;
  public config: SwiperOptions = null;
  public category: Category = null;
  public subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialog,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public catalogService: CatalogService,
    public canonicalService: CanonicalService,
    public trackingService: TrackingService,
    public settings: AppSettings,
    public spinner: NgxSpinnerService) { }

  ngOnInit(): void {
    if (this.products?.length < 8) {
      const dupProducts: Product[] = [];
      let numDuplicates = 2;
      if (this.products?.length < 2) {
        numDuplicates = 8;
      } else if (this.products.length < 3) {
        numDuplicates = 4;
      } else if (this.products.length < 4) {
        numDuplicates = 3;
      }
      for (var i = 0; i <= numDuplicates; i++) {
        this.products.forEach((product) => {
          dupProducts.push(product);
        });
      }
      this.products = dupProducts;
    }
  }

  ngAfterViewInit(): void {
    this.configSwiper();
  }

  ngAfterViewChecked(): void {
    this.initSwiper();
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
  }

  configSwiper(): void {
    this.config = {
      resizeObserver: true,
      slidesPerView: 2,
      spaceBetween: 16,
      pagination: false,
      loop: true,
      effect: 'slide',
      breakpoints: {
        480: { slidesPerView: this.count480 },
        740: { slidesPerView: this.count740 },
        960: { slidesPerView: this.count960 },
        1280: { slidesPerView: this.count1280 },
        1700: { slidesPerView: this.count1700 }
      }
    };
    this.swiper?.forEach((swiper) => {
      Object.assign(swiper?.nativeElement, this.config);
    });
  }

  initSwiper(): void {
    this.swiper?.forEach((swiper) => {
      swiper.nativeElement?.initialize();
    });
  }

  public productClick(product: Product, position: number, pageNumber: number): void {
    this.trackingService.pushProductClick(product, this.listId, 'Carousel - ' + this.carouselName, position + (pageNumber * environment.pageSize));
    this.subscriptions.push(this.canonicalService.productCanonical(product).subscribe(
      res => {
        if (res) {
          this.router.navigate([`${res}/${product.seName}`]);
        } else {
          this.router.navigate([`/products/${product.id}`]);
        }
      }
    ))
  }

  public openProductDialog(productSimple: Product, position: number, pageNumber: number): void {
    this.spinner.show();
    this.trackingService.pushProductClick(productSimple, this.listId, 'Carousel - ' + this.carouselName, position + (pageNumber * environment.pageSize));
    this.subscriptions.push(this.catalogService.getProductDetail(productSimple.id).subscribe(
      res => {
        this.spinner.hide();
        const product = res as ProductDetail;
        const dialogRef = this.dialog.open(ProductDialogComponent, {
          data: { product, category: null, listId: this.listId },
          panelClass: 'product-dialog',
          direction: (this.settings.rtl) ? 'rtl' : 'ltr'
        });
        dialogRef.afterClosed().subscribe(productDialog => {
          if (productDialog) {
            this.router.navigate([product.seName], { relativeTo: this.activatedRoute });
          }
        });
      }));
  }

  public imageStateChange(event: StateChange, product: Product, position: number, pageNumber: number): void {
    if (event?.reason === 'loading-succeeded') {
      this.trackingService.pushImpression(product, this.listId, 'Carousel - ' + this.carouselName, position + (pageNumber * environment.pageSize));
    }
  }

  public slideNext() {
    this.swiper?.forEach((swiper) => {
      swiper?.nativeElement?.swiper?.slideNext(500);
    });
  }

  public slidePrev() {
    this.swiper?.forEach((swiper) => {
      swiper?.nativeElement?.swiper?.slidePrev(500);
    });
  }

  public productSpecificationAttribute(product: Product, specificationAttributeName: string): string {
    return product?.specifications?.find(s => s.specificationAttributeName === specificationAttributeName)?.valueRaw;
  }
}
